import { useTranslation } from 'react-i18next'

import { EMPTY_LIST } from '../../../constants/common'
import { useInlineNotifications } from '../../../hooks'
import { FormikForm } from '../../Formik'
import { createLabel, updateLabel } from '../Label.api'
import { getRemovedEntitiesAssignmentTotal } from '../Label.utils'
import { LabelEntityAssignmentsConfirmationModal } from '../LabelEntityAssignmentsConfirmationModal'

import { FormFields } from './FormFields'
import {
  applyServerValidationErrors,
  getInitialValues,
  getPayload,
  getValidationSchema,
} from './LabelForm.utils'

import * as styles from './LabelForm.module.css'

function LabelForm({
  FormContainer,
  containerProps,
  initValues,
  setModal,
  onDeleteSuccess,
  onSuccess,
  ...restProps
}) {
  const { t } = useTranslation()

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const initialValues = getInitialValues(initValues, t)
  const validationSchema = getValidationSchema()

  function submitLabelRequest(sendRequest, { setErrors, setSubmitting }) {
    setSubmitting(true)

    return sendRequest()
      .then(({ data }) => {
        onSuccess?.(data)
      })
      .catch(error => {
        applyServerValidationErrors(error, setErrors)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  function editLabel(payload, formikHelpers, extra) {
    return submitLabelRequest(
      () => updateLabel(initValues.id, { ...payload, ...extra }),
      formikHelpers
    )
  }

  function handleDismissAssignmentsConfirmation(formikHelpers) {
    setModal(null)
    formikHelpers.setSubmitting(false)
  }

  function handleConfirmAssignmentsRemoval(payload, formikHelpers, extra) {
    setModal(null)
    editLabel(payload, formikHelpers, extra)
  }

  function openAssignmentsConfirmation(
    payload,
    formikHelpers,
    assignmentTotal
  ) {
    setModal(
      <LabelEntityAssignmentsConfirmationModal
        assignmentCount={assignmentTotal}
        onCancel={() => handleDismissAssignmentsConfirmation(formikHelpers)}
        onClose={() => handleDismissAssignmentsConfirmation(formikHelpers)}
        onSubmit={extra =>
          handleConfirmAssignmentsRemoval(payload, formikHelpers, extra)
        }
      />
    )
  }

  function handleSubmit(values, formikHelpers) {
    const payload = getPayload(values)

    if (!initValues?.id) {
      return submitLabelRequest(() => createLabel(payload), formikHelpers)
    }

    const assignmentTotal = getRemovedEntitiesAssignmentTotal({
      assignmentsCount: initValues.assignments_count,
      nextEntities: payload.entities ?? EMPTY_LIST,
      prevEntities: initValues.entities,
    })

    if (assignmentTotal && setModal) {
      openAssignmentsConfirmation(payload, formikHelpers, assignmentTotal)

      return
    }

    return editLabel(payload, formikHelpers)
  }

  return (
    <FormikForm
      enableReinitialize
      Container={FormContainer}
      containerProps={containerProps}
      formProps={{
        className: styles.formBody,
      }}
      initialValues={initialValues}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <FormFields
        {...restProps}
        initValues={initValues}
        isEdit={!!initValues?.id}
        notifications={notifications}
        setModal={setModal}
        onDeleteSuccess={onDeleteSuccess}
        onRemoveNotification={handleRemoveNotification}
        onSetNotification={handleAddNotification}
      />
    </FormikForm>
  )
}

export default LabelForm
